import { useCallback } from "react";
import { HR_RECRUITMENT_STAGE_OPTIONS } from "../dashboard/Config";

export default function HrAdminActions({
  hrEligibleIdSet,
  setHrRecruitmentStageById,
  setHrAttendanceOverrideById,
  normalizeUserStatus,
  setHrAttendanceImporting,
  hrAnnouncementDraft,
  setHrAnnouncementDraft,
  setHrAnnouncements,
  profileDisplayName,
  setHrDocumentUploading,
  setHrDocumentUploads,
  locale,
  setAdminError,
  setAdminToast
}) {
  const updateHrRecruitmentStage = useCallback((rowId, stageValue) => {
    const id = Number(rowId || 0);
    if (!hrEligibleIdSet.has(id)) {
      return;
    }

    const normalizedStage = HR_RECRUITMENT_STAGE_OPTIONS.includes(String(stageValue || "").trim())
      ? String(stageValue || "").trim()
      : "needs_position";

    setHrRecruitmentStageById((prev) => ({
      ...(prev || {}),
      [id]: normalizedStage
    }));
    setAdminToast(locale === "zh" ? "招聘阶段已更新" : "Recruitment stage updated");
  }, [hrEligibleIdSet, locale, setAdminToast, setHrRecruitmentStageById]);

  const importHrAttendance = useCallback(async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    setHrAttendanceImporting(true);
    try {
      const content = await file.text();
      const rows = String(content || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

      if (!rows.length) {
        setAdminError(locale === "zh" ? "导入文件为空" : "Attendance file is empty");
        return;
      }

      const sample = rows[0];
      const delimiter = sample.includes("\t") ? "\t" : ",";
      const rawColumns = rows[0].split(delimiter).map((item) => String(item || "").trim());
      const normalizedColumns = rawColumns.map((item) => item.toLowerCase());

      const idIndex = normalizedColumns.findIndex(
        (item) => item === "id" || item === "user_id" || item.includes("用户")
      );
      const statusIndex = normalizedColumns.findIndex(
        (item) => item === "status" || item.includes("状态")
      );
      const activeAtIndex = normalizedColumns.findIndex(
        (item) => item === "lastactiveat" || item === "last_active_at" || item.includes("活跃") || item.includes("active")
      );

      const hasHeader = idIndex >= 0;
      const startIndex = hasHeader ? 1 : 0;
      let importedCount = 0;

      setHrAttendanceOverrideById((prev) => {
        const next = { ...(prev || {}) };

        for (let index = startIndex; index < rows.length; index += 1) {
          const parts = rows[index].split(delimiter).map((item) => String(item || "").trim());
          const idRaw = idIndex >= 0 ? parts[idIndex] : parts[0];
          const rowId = Number(idRaw || 0);

          if (!Number.isFinite(rowId) || rowId <= 0 || !hrEligibleIdSet.has(rowId)) {
            continue;
          }

          const statusRaw = statusIndex >= 0 ? parts[statusIndex] : parts[1];
          const lastActiveRaw = activeAtIndex >= 0 ? parts[activeAtIndex] : parts[2];

          next[rowId] = {
            status: normalizeUserStatus(statusRaw || "active"),
            lastActiveAt: String(lastActiveRaw || "").trim() || next[rowId]?.lastActiveAt || ""
          };
          importedCount += 1;
        }

        return next;
      });

      if (importedCount > 0) {
        setAdminError("");
        setAdminToast(
          locale === "zh"
            ? `已导入 ${importedCount} 条考勤记录`
            : `Imported ${importedCount} attendance records`
        );
      } else {
        setAdminError(
          locale === "zh"
            ? "未匹配到符合条件的员工记录（仅支持有岗位的管理员及以上）"
            : "No eligible staff records were matched (admin-and-above with position only)"
        );
      }
    } catch (err) {
      setAdminError(err?.message || (locale === "zh" ? "考勤导入失败" : "Attendance import failed"));
    } finally {
      setHrAttendanceImporting(false);
      if (event?.target) {
        event.target.value = "";
      }
    }
  }, [
    hrEligibleIdSet,
    locale,
    normalizeUserStatus,
    setAdminError,
    setAdminToast,
    setHrAttendanceImporting,
    setHrAttendanceOverrideById
  ]);

  const publishHrAnnouncement = useCallback(() => {
    const content = String(hrAnnouncementDraft || "").trim();
    if (!content) {
      setAdminError(locale === "zh" ? "请填写公告内容" : "Please enter announcement content");
      return;
    }

    setHrAnnouncements((prev) => [
      {
        id: `hr-announcement-${Date.now()}`,
        content,
        createdAt: new Date().toISOString(),
        author: profileDisplayName
      },
      ...(Array.isArray(prev) ? prev : [])
    ]);
    setHrAnnouncementDraft("");
    setAdminError("");
    setAdminToast(locale === "zh" ? "公告已发布" : "Announcement published");
  }, [
    hrAnnouncementDraft,
    locale,
    profileDisplayName,
    setAdminError,
    setAdminToast,
    setHrAnnouncementDraft,
    setHrAnnouncements
  ]);

  const deleteHrAnnouncement = useCallback((announcementId) => {
    const targetId = String(announcementId || "").trim();
    if (!targetId) {
      return;
    }

    setHrAnnouncements((prev) => (Array.isArray(prev) ? prev.filter((item) => String(item?.id || "") !== targetId) : []));
    setAdminToast(locale === "zh" ? "公告已删除" : "Announcement removed");
  }, [locale, setAdminToast, setHrAnnouncements]);

  const uploadHrDocument = useCallback(async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    setHrDocumentUploading(true);
    try {
      const nextItem = {
        id: `hr-doc-${Date.now()}`,
        name: String(file.name || "").trim() || "document",
        type: String(file.type || "").trim() || "-",
        size: Number(file.size || 0),
        uploadedAt: new Date().toISOString(),
        uploadedBy: profileDisplayName
      };

      setHrDocumentUploads((prev) => [nextItem, ...(Array.isArray(prev) ? prev : [])]);
      setAdminError("");
      setAdminToast(locale === "zh" ? "文档上传完成" : "Document uploaded");
    } catch (err) {
      setAdminError(err?.message || (locale === "zh" ? "文档上传失败" : "Document upload failed"));
    } finally {
      setHrDocumentUploading(false);
      if (event?.target) {
        event.target.value = "";
      }
    }
  }, [
    locale,
    profileDisplayName,
    setAdminError,
    setAdminToast,
    setHrDocumentUploading,
    setHrDocumentUploads
  ]);

  const deleteHrDocument = useCallback((docId) => {
    const targetId = String(docId || "").trim();
    if (!targetId) {
      return;
    }

    setHrDocumentUploads((prev) => (Array.isArray(prev) ? prev.filter((item) => String(item?.id || "") !== targetId) : []));
    setAdminToast(locale === "zh" ? "文档已移除" : "Document removed");
  }, [locale, setAdminToast, setHrDocumentUploads]);

  return {
    updateHrRecruitmentStage,
    importHrAttendance,
    publishHrAnnouncement,
    deleteHrAnnouncement,
    uploadHrDocument,
    deleteHrDocument
  };
}
